import DigitalTwin from "@synergy/digital-twin";
import { Drawer } from "antd";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAuthContext } from "../../../../../../contexts/AuthContext";
import query from "../../../../../../server/constants.api";
import logger from "../../../../../../server/server";
import useFetch from "../../../../../../server/useFetch";
import { PageContent } from "../../../../../../styles/global/Pages.styled";
import { REACT_APP_MAPS_API_KEY } from "../../../../../../utils/config/configs";
import { DigitalTwinDrawerDetail } from "../../../../../Pages/DigitalTwinDrawerDetail";
import Spinner from "../../../../Atomics/Spinner";
import { Widget } from "../../../../Atomics/Widget";
import { elementTypes } from "./DigitalTwinConfig/DigitalTwinConfig";
import { DigitalTwinMetrics } from "./DigitalTwinMetrics/DigitalTwinMetrics";
const DrawerContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DigitalTwinContainer = styled.div`
  height: 600px;
  display : flex;
  justify-content: center;
  align-items: center;
`;

/**
 * @component DigitalTwin
 * @description Renders a digital twin graph for a building.
 * @param {Object} props - The component props.
 * @param {Object} props.item - The building object.
 * @returns {JSX.Element} The rendered DigitalTwin component.
 */
export const DigitalTwinPage = ({ item, ...props }) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [itemSelected, setItemSelected] = useState(null);

  const [viewMap, setViewMap] = useState(false);
  const [centralNode, setCentralNode] = useState(item);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [levelsToFetch, setLevelsToFetch] = useState(1);
  const [isRendering, setIsRendering] = useState(false);
  const { t } = useTranslation();
  const { tenant } = useAuthContext();
  const [showMetricButton, setShowMetricButton] = useState(false);

  useEffect(() => {
    logger.info("User entered BuildingIncidents successfully!");
  }, []);
  const updateCentralNode = (node) => {
    setCentralNode(node);
  };

  const {
    data: providersData,
    refetch: refetchProvidersData,
    isFetching: isFetchingProvidersData,
  } = useFetch(
    query.path.providersData,
    "providersData",
    {
      id: centralNode.id,
      level: levelsToFetch,
    },
    item?.id ?? false,
    null,
    `${query.middlewareURL}/digital-twin`
  );

  const {
    data: dependentsData,
    refetch: refetchDependentsData,
    isFetching: isFetchingDependentsData,
  } = useFetch(
    query.path.dependentsData,
    "dependentsData",
    {
      id: centralNode.id,
      level: levelsToFetch,
    },
    item?.id ?? false,
    null,
    `${query.middlewareURL}/digital-twin`
  );

  const {
    data: federationsClassesData,
    isFetching: isFetchingFederationsClassesData,
  } = useFetch(
    "/federations/classes",
    "federationsClassesData",
    {
      tenant: tenant
    },
    item?.id ?? false,
    null,
    `${query.middlewareURL}/items`
  )

  /**
   * Show the metric button if the item selected has a federation class.
   */
  useEffect(() => {
    if (!federationsClassesData || !itemSelected) return
    const isMetricButtonVisible = federationsClassesData?.includes(itemSelected?.class);
    setShowMetricButton(isMetricButtonVisible);
  }, [federationsClassesData, itemSelected]);

  /**
   * Refetch the data when the central node changes.
   */
  useEffect(() => {
    setIsRendering(true);

    refetchDependentsData();
    refetchProvidersData();
  }, [centralNode, refetchDependentsData, refetchProvidersData, levelsToFetch]);



  const updateMapVisibility = (isVisible) => {
    if (!isFetchingDependentsData && !isFetchingProvidersData) {
      setIsMapVisible(isVisible);
    }
  }

  /**
   * Add the names of the nodes to the edge
   * @param {*} edge The edge to add the names
   * @param {*} nodes The nodes to get the names
   * @returns The edge with the names
   */
  const addNodeNamesToEdge = (edge, nodes) => {
    if (!edge || !nodes) return edge;
    const sourceNode = nodes.find((node) => node.id === edge.outV);
    const targetNode = nodes.find((node) => node.id === edge.inV);

    if (sourceNode && targetNode) {
      edge.outVName = sourceNode.name;
      edge.inVName = targetNode.name;
    }
    return edge;
  }

  return (
    <PageContent className={clsx("building_room_details", props?.className)}>
      {isPopUpOpen &&
        <DigitalTwinMetrics
          isPopUpOpen={isPopUpOpen}
          handleClose={() => setIsPopUpOpen(false)}
          nodeId={itemSelected?.id}
        />}
      <Widget data={{
        background: "white",
        dropdowns: [
          {
            data: [
              {
                label: t("Level 1"),
                value: 1,
              },
              {
                label: t("Level 2"),
                value: 2,
              },
              {
                label: t("Level 3"),
                value: 3,
              },
            ],
            value: levelsToFetch,
            functionChange: (value) => setLevelsToFetch(value),
            isToDisable: isRendering,
          },
        ],
        switch: [{
          title: t("View Map"),
          onClick: () => {
            setViewMap(!viewMap);
          },
          isToDisable: !isMapVisible || isRendering,
          isToDisabledText: !isMapVisible ? t("Without coordinates") : "",
          display: "flex"
        }],

      }}>
        {isRendering && <Spinner stroke="green" style={{ position: "absolute", top: "50%", left: "50%" }} />}
        <DigitalTwinContainer
          style={{ visibility: isRendering ? "hidden" : "visible" }}
        >
          <DigitalTwin
            isViewMap={viewMap}
            centralNode={centralNode}
            updateCentralNode={updateCentralNode}
            providersNodesData={providersData?.providers}
            providersEdgesData={providersData?.relationships}
            dependentsNodesData={dependentsData?.dependents}
            dependentsEdgesData={dependentsData?.relationships}
            isFetchingProvidersData={isFetchingProvidersData}
            isFetchingDependentsData={isFetchingDependentsData}
            onElementClick={(element, elementType) => {
              setOpenDrawer(!!element);

              let newItemSelected = { ...element, elementType };

              if (elementType === elementTypes.edge) {
                const edge = addNodeNamesToEdge(element, [...providersData?.providers, ...dependentsData?.dependents, centralNode]);
                newItemSelected = { ...edge, elementType };
              }

              setItemSelected(newItemSelected);
            }}
            onMapVisibilityChange={(isVisible) => {
              updateMapVisibility(isVisible);
            }}
            setIsRendering={setIsRendering}
            isDrawerOpen={openDrawer}
            mapApiKey={REACT_APP_MAPS_API_KEY}
          />
        </DigitalTwinContainer>
      </Widget>
      <DrawerContainer id="drawer-container">
        <Drawer
          title={itemSelected?.name || itemSelected?.label}
          onClose={() => {
            setOpenDrawer(false);
          }}
          open={openDrawer}
          size="default"
          getContainer={() => document.getElementById("drawer-container")}
          style={{
            position: "absolute",
            top: "88px",
            height: "-webkit-fill-available",
          }}
          mask={false}
        >
          <DigitalTwinDrawerDetail itemSelected={itemSelected} openMetricsPopUp={() => setIsPopUpOpen(true)}
            showMetricButton={showMetricButton}
          />
        </Drawer>
      </DrawerContainer>
    </PageContent >
  );
};
